import { t } from "@/lib/i18n";

// Map exact mission descriptions from the server to translation keys.
// Keep this simple and literal — no pattern matching or type fallbacks.
const descriptionToKeyMap: Record<string, string> = {
  // Keep empty by default to show server-provided descriptions as-is.
  // Add entries only for truly fixed phrases that have localized copies.
};

function shortHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

function slugFromDescription(desc: string): string {
  const normalized = desc
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return normalized || `desc_${shortHash(desc)}`;
}

/**
 * Translate a mission description exactly as provided by the backend.
 * Order of resolution:
 * 1) Exact mapping in descriptionToKeyMap
 * 2) Slug-based key: missions.descriptions.<slugified description>
 * 3) Slug+hash key: missions.descriptions.<slug>_<hash> (handles rare slug collisions)
 * 4) Fallback: original description
 * The second parameter is ignored to avoid type-based inference.
 */
export function translateMissionDescription(
  description: string,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _missionType?: string,
): string {
  const trimmed = description?.trim?.() ?? description;

  // 1) Exact mapping
  const exactKey = descriptionToKeyMap[trimmed];
  if (exactKey) {
    const translated = t(exactKey);
    if (translated !== exactKey) return translated;
  }

  // 2) Slug-based key generated from description text
  const slug = slugFromDescription(trimmed);
  const baseKey = `missions.descriptions.${slug}`;
  let translated = t(baseKey);
  if (translated !== baseKey) return translated;

  // 3) Optional collision key with hash suffix
  const hashKey = `missions.descriptions.${slug}_${shortHash(trimmed)}`;
  translated = t(hashKey);
  if (translated !== hashKey) return translated;

  // 4) Fallback to original text
  return description;
}
